import { useEffect, useMemo, useState } from "react";
import { ToastService } from "../../../shared/bootstrap/Toast";
import { ImportTemplateListResponse } from "../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../services/TranslationService";
import {
  additionalDefsToFieldData,
  TemplateField
} from "./ImportTemplateEditBase";

import ImportTemplateService, {
  getPersonKeyLabel,
  ImportTemplateId
} from "../../../../services/ImportTemplateService";
import FloatingPanelService from "../../../shared/FloatingPanel";
import { RequiredManager } from "../../../shared/RequieredManager";
import { FloatingPanelFooter } from "../../../shared/components/FloatingPanelFooter";
import { InvoiceFields } from "../../../../services/InvoiceService";
import {
  ClientExportField,
  ClientFields
} from "../../../../services/ClientService";
import Loading from "../../../shared/components/Loading";
import { handleErrorWithToast } from "../../../../utils/RequestUtils";
import ModalService from "../../../shared/bootstrap/modal/Modal";
import FieldSelectColumnList from "./Components/FieldSelectColumnList";
import { ImportExportGetResponse } from "../../../../entities/import/ImportExportGet";
import CompanyService, { Entities } from "../../../../services/CompanyService";

const ImportTemplateEditClientExportTemplate = ({
  data,
  reload,
  importsList = undefined,
  entityId = undefined
}: {
  data?: ImportTemplateListResponse.Item;
  reload: () => void;
  importsList?: ImportTemplateListResponse;
  entityId?: number;
}) => {
  const { translate } = TranslationService;
  const [loading, setLoading] = useState(true);
  const [submiting, setSubmiting] = useState(false);
  const requiredManager = useMemo(() => new RequiredManager(), []);
  const [model, setModel] = useState<Partial<ImportExportGetResponse.Item>>(
    () => ({ Sheet: "num-1", Entity: entityId!, entity: entityId! })
  );

  const Header = () => (
    <div className="mx-4 mt-4 d-flex align-items-center justify-content-between">
      <h5>{translate.ConfigureAutomaticClientUpdateProcess} </h5>
    </div>
  );

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const result = await handleErrorWithToast(
        ImportTemplateService.getExportTemplate(data!.ImportTemplateID)
      );

      const columnsModel = result.list.map(
        (rl: ImportExportGetResponse.Item) => {
          const name = rl.AdditionalDefinition
            ? "additional-" + rl.AdditionalDefinitionID
            : rl.Field?.toString();
          const ix = rl.Position;
          return { ix, name, pattern: "", entity: rl.entity };
        }
      );
      setModel((x) => ({ ...x, Columns: [...columnsModel] }));
      setLoading(false);
    };
    getData();
  }, [data]);

  const onSubmit = async () => {
    if (submiting) {
      return;
    }
    if (!requiredManager.validate()) {
      ToastService.showToast(translate.MissingRequiredFields);
      return;
    }
    setSubmiting(true);

    const list = (model as any).Columns.filter(
      (c: ImportTemplateListResponse.Column) => c.ix
    ).map((mc: any) => {
      const isAdditional = mc.name.toString().includes("additional");

      if (isAdditional) {
        return {
          Field: mc.name.toString().replace("additional-", ""),
          Position: mc.ix,
          Entity: mc.entity ?? 0
        };
      }

      if (mc.name < 0) {
        return {
          Field: mc.name,
          Position: parseInt(mc.ix),
          // El campo monto de facturas (-3) se guarda con Entity 0.
          Entity:
            parseInt(mc.name) === InvoiceFields.amount ? 0 : (mc.entity ?? 0)
        };
      }

      return {
        Field:
          ClientExportField[mc.name] ||
          ClientFields[mc.name] ||
          InvoiceFields[mc.name] ||
          mc.name,
        Position: parseInt(mc.ix),
        Entity: mc.entity ?? 0
      };
    });

    const result = await ImportTemplateService.setExportTemplate(
      data!.ImportTemplateID,
      list
    );
    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }

    ToastService.showToast(translate.ImportTemplateSaved, undefined, "success");
    setSubmiting(false);
    reload();
    FloatingPanelService.hidePanel();
  };

  const clientFields: TemplateField[] = useMemo(() => {
    if (entityId !== undefined || entityId) {
      let resultFields = ImportTemplateService.getExportTemplateFields(
        Entities.Client
      );
      if (entityId === ImportTemplateId.PersonAutomaticUpdate) {
        const personKey = CompanyService.getPersonKey();
        const personKeyDefinition =
          CompanyService.getAdditionalDefinitions().find(
            (x) =>
              x.Entity === Entities.Client &&
              x.AdditionalDefinitionID === personKey
          );
        if (personKeyDefinition) {
          const [personKeyField] = additionalDefsToFieldData([
            personKeyDefinition
          ]);
          resultFields = [
            { ...personKeyField, required: true },
            ...resultFields.filter((x) => x.fieldId !== personKeyField.fieldId)
          ];
        }
      }
      resultFields = resultFields.map((x) => ({
        ...x,
        entity: Entities.Invoice
      }));
      ModalService.hideModal();
      return resultFields;
    } else {
      return [];
    }
  }, [entityId]);

  const invoiceFields: TemplateField[] = useMemo(() => {
    if (entityId !== undefined || entityId) {
      let resultFields = ImportTemplateService.getExportTemplateFields(
        Entities.Invoice
      );
      if (entityId === ImportTemplateId.PersonAutomaticUpdate) {
        // Réplica del CASO B legacy (PersonExportTemplate.cshtml): Fieldname
        // "contact", siempre opcional, resuelto a (int)IOs.fields.client.
        resultFields.unshift({
          fieldId: "contact",
          key: InvoiceFields.client.toString(),
          label: getPersonKeyLabel(),
          required: false
        });
      }
      resultFields = resultFields.map((x) => ({
        ...x,
        entity: Entities.Invoice
      }));
      ModalService.hideModal();
      return resultFields;
    } else {
      return [];
    }
  }, [entityId]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Header />
      <div className="floatingBody p-4">
        <FieldSelectColumnList
          model={model}
          setModel={setModel}
          clientFields={clientFields}
          invoiceFields={invoiceFields}
          requiredFieldIds={clientFields
            .filter((x) => x.required)
            .map((x) => x.fieldId)}
          requiredManager={requiredManager}
        />
      </div>
      <FloatingPanelFooter>
        <button className="btn btn-primary" onClick={onSubmit}>
          {translate.Save}
          {submiting && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </FloatingPanelFooter>
    </>
  );
};
export default ImportTemplateEditClientExportTemplate;
